// client/src/components/AddAccountModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;

function AddAccountModal({ show, handleClose, refreshAccounts, setSubmissionMessage }) {
  // State for form fields
  const [formData, setFormData] = useState({
    role: 'Admin', // Default role for new account generation
    username: '', // REQUIRED: no empty user references
    associatedDealerId: '', // Required if role is 'Dealer'
  });
  // Inline validation state
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  // State for UI feedback
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [fetchDealersError, setFetchDealersError] = useState('');

  const roles = ['Admin', 'PreProduction', 'Manufacturing', 'Shipping', 'Dealer', 'Employee', 'Supervisor'];

  // Refactored state reset function to avoid duplication
  const resetModalState = useCallback(() => {
    setFormData({
      role: 'Admin',
      username: '',
      associatedDealerId: '',
    });
    setTouched({});
    setErrors({});
    setSubmissionMessage('', ''); // Clear any previous messages from parent
    setFetchDealersError('');
  }, [setSubmissionMessage]);

  // Effect to reset form data and messages when the modal is shown
  useEffect(() => {
    if (show) {
      resetModalState();
    }
  }, [show, resetModalState]);

  // Effect to fetch dealers when the role is 'Dealer' and modal is open
  useEffect(() => {
    const fetchDealersData = async () => {
      // Only fetch if the role is 'Dealer'
      if (formData.role === 'Dealer') {
        setLoadingDealers(true);
        setFetchDealersError('');
        try {
          const response = await fetch(`${API_ROOT_URL}/dealers`, {
            method: 'GET',
            headers: getAuthHeaders()
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          data.sort((a, b) => a.dealerName.localeCompare(b.dealerName));
          setDealers(data);
          // If there are no dealers, clear the selected dealer ID
          if (data.length === 0) {
            setFormData(prev => ({ ...prev, associatedDealerId: '' }));
          }
        } catch (error) {
          console.error('Error fetching dealers for account creation:', error);
          setFetchDealersError('Failed to load dealer options. Please ensure backend is running or add dealers first.');
        } finally {
          setLoadingDealers(false);
        }
      } else {
        setDealers([]); // Clear dealers if not a Dealer role
        setFormData(prev => ({ ...prev, associatedDealerId: '' })); // Clear dealer ID when role changes
      }
    };
    if (show) { // Only fetch when modal is shown
      fetchDealersData();
    }
  }, [formData.role, show]);

  // ---- Inline validation helpers ----
  const buildErrors = (data) => {
    const e = {};
    // Username required (no empty user references)
    const name = (data.username || '').trim();
    if (!name) e.username = 'User’s full name is required.';
    else if (name.length < 2) e.username = 'Name must be at least 2 characters.';

    // For Dealer role, associated dealer required
    if (data.role === 'Dealer') {
      if (!data.associatedDealerId) {
        e.associatedDealerId = 'Please select an associated dealer.';
      }
      if (!loadingDealers && dealers.length === 0) {
        e.associatedDealerId = 'No dealers available. Add dealers first.';
      }
    }

    return e;
  };

  const validateAndSet = (next = formData) => {
    const e = buildErrors(next);
    setErrors(e);
    return e;
  };

  const setFieldTouched = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  // Handle input changes for form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldTouched(name);
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      return next;
    });
    // Re-validate on change
    validateAndSet({ ...formData, [name]: value });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFieldTouched(name);
    validateAndSet();
  };

  const invalid = (name) => !!(touched[name] && errors[name]);
  const feedback = (name) => (touched[name] && errors[name]) ? errors[name] : '';

  // Handle form submission to create a new account ID
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmissionMessage('', ''); // Clear any previous messages from parent

    // Touch important fields before validating
    const toTouch = { username: true };
    if (formData.role === 'Dealer') toTouch.associatedDealerId = true;
    setTouched(prev => ({ ...prev, ...toTouch }));

    const errObj = validateAndSet(formData);
    if (Object.keys(errObj).length > 0) {
      setSubmissionMessage('Please correct the highlighted fields.', 'danger');
      setLoading(false);
      return;
    }

    const dataToSend = {
      role: formData.role,
      username: formData.username.trim(),
    };

    // Add associatedDealerId only if the role is 'Dealer'
    if (formData.role === 'Dealer') {
      dataToSend.associatedDealerId = formData.associatedDealerId;
    }

    try {
      const response = await fetch(`${API_ROOT_URL}/accounts`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
        },
        body: JSON.stringify(dataToSend),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmissionMessage(result.message || 'Account ID generated successfully!', 'success');
        refreshAccounts(); // Refresh the list of accounts on the parent page
        onHide(); // Close modal immediately upon successful submission
      } else {
        setSubmissionMessage(result.message || 'Failed to generate Account ID.', 'danger');
      }
    } catch (error) {
      console.error('Error adding account:', error);
      setSubmissionMessage('Network error or server is unreachable. Please try again.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Reset form and close modal
  const onHide = () => {
    resetModalState();
    handleClose();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Generate New Account ID</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetchDealersError && <Alert variant="danger">{fetchDealersError}</Alert>}

        <Form onSubmit={handleSubmit} noValidate>
          <Form.Group className="mb-3" controlId="formRole">
            <Form.Label>Account Role <span className="text-danger">*</span></Form.Label>
            <Form.Select
              name="role"
              value={formData.role}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              disabled={loading}
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formUsername">
            <Form.Label>User's Full Name (for administrator's reference) <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={invalid('username')}
              placeholder="e.g., John Doe or Department Name"
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">
              {feedback('username')}
            </Form.Control.Feedback>
          </Form.Group>

          {formData.role === 'Dealer' && (
            <Form.Group className="mb-3" controlId="formAssociatedDealerId">
              <Form.Label>Associated Dealer <span className="text-danger">*</span></Form.Label>
              {loadingDealers ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" /> Loading Dealers...
                </div>
              ) : (
                <Form.Select
                  name="associatedDealerId"
                  value={formData.associatedDealerId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={invalid('associatedDealerId')}
                  required={formData.role === 'Dealer'}
                  disabled={dealers.length === 0 || loading}
                >
                  <option value="">{dealers.length > 0 ? "Select an Associated Dealer" : "No Dealers Available"}</option>
                  {dealers.map((dealer) => (
                    <option key={dealer.dealerId} value={dealer.dealerId}>
                      {dealer.dealerName} ({dealer.dealerId})
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Control.Feedback type="invalid">
                {feedback('associatedDealerId')}
              </Form.Control.Feedback>
              {dealers.length === 0 && !loadingDealers && (
                <Alert variant="warning" className="mt-2">
                  No dealers found. Please add dealers via the Dealers page before creating a Dealer account.
                </Alert>
              )}
            </Form.Group>
          )}

          <Button
            variant="primary"
            type="submit"
            disabled={
              loading ||
              (formData.role === 'Dealer' && (dealers.length === 0 || !formData.associatedDealerId)) ||
              !(formData.username || '').trim() // prevent empty user reference
            }
            className="w-100 mt-3"
          >
            {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Generate Account ID'}
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddAccountModal;
